import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { FH_FIELD, FH_FIELD_ERROR, FH_FIELD_TYPE, FH_FIELD_VALIDATION, FH_WARN } from './company-form.constants';
import { CompanyFieldDescriptor } from './company-form.types';

// Validators built from the merged field config. Every factory skips empty values — that is
// `Validators.required`'s job, and `required` is applied dynamically by the rules engine so a
// conditionally-required field has exactly one code path.

const URL_PATTERN = /^https?:\/\/[^\s]+$/i;
// Deliberately permissive: the backend is the authority on deliverability.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

// The backend regex is a Go pattern. Most are portable, but RE2 has constructs `new RegExp`
// rejects — compiling once here means a bad pattern degrades to a no-op instead of throwing on
// every keystroke.
export function fhRegexValidator(pattern: string): ValidatorFn {
  let compiled: RegExp | null = null;
  try {
    compiled = new RegExp(pattern);
  } catch (error) {
    console.warn(FH_WARN.INVALID_REGEX, pattern);
    compiled = null;
  }

  return (control: AbstractControl): ValidationErrors | null => {
    if (!compiled || isEmpty(control.value)) {
      return null;
    }
    return compiled.test(String(control.value)) ? null : { [FH_FIELD_ERROR.PATTERN]: true };
  };
}

export function fhEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmpty(control.value)) {
      return null;
    }
    return EMAIL_PATTERN.test(String(control.value).trim()) ? null : { [FH_FIELD_ERROR.EMAIL]: true };
  };
}

export function fhUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmpty(control.value)) {
      return null;
    }
    return URL_PATTERN.test(String(control.value).trim()) ? null : { [FH_FIELD_ERROR.URL]: true };
  };
}

export function fhDecimalsValidator(maxDecimals: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmpty(control.value)) {
      return null;
    }
    const raw = String(control.value);
    const separatorIndex = raw.indexOf('.');
    if (separatorIndex === -1) {
      return null;
    }
    const decimals = raw.length - separatorIndex - 1;
    return decimals > maxDecimals
      ? { [FH_FIELD_ERROR.DECIMALS]: { max: maxDecimals, actual: decimals } }
      : null;
  };
}

// Flags every row sharing a threshold with another row. Sets the error on the offending
// `rebate_threshold` controls so the message renders inline, and returns an array-level error so
// `form.invalid` blocks Submit.
export function fhDuplicateThresholdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const array = control as FormArray;
    if (!array || !array.controls) {
      return null;
    }

    const indexesByThreshold = new Map<string, number[]>();
    array.controls.forEach((row, index) => {
      const value = (row as FormGroup).get(FH_FIELD.REBATE_THRESHOLD)?.value;
      if (isEmpty(value)) {
        return;
      }
      const key = String(Number(value));
      const existing = indexesByThreshold.get(key) ?? [];
      existing.push(index);
      indexesByThreshold.set(key, existing);
    });

    const duplicateIndexes: number[] = [];
    indexesByThreshold.forEach(indexes => {
      if (indexes.length > 1) {
        duplicateIndexes.push(...indexes);
      }
    });

    // Write the per-row error without clobbering required/min errors already on the control.
    array.controls.forEach((row, index) => {
      const thresholdControl = (row as FormGroup).get(FH_FIELD.REBATE_THRESHOLD);
      if (!thresholdControl) {
        return;
      }
      const errors = { ...(thresholdControl.errors ?? {}) };
      const isDuplicate = duplicateIndexes.indexOf(index) !== -1;
      if (isDuplicate) {
        errors[FH_FIELD_ERROR.DUPLICATE_THRESHOLD] = true;
      } else {
        delete errors[FH_FIELD_ERROR.DUPLICATE_THRESHOLD];
      }
      const next = Object.keys(errors).length ? errors : null;
      // setErrors triggers a status update; emitEvent:false keeps it out of valueChanges.
      thresholdControl.setErrors(next, { emitEvent: false });
    });

    return duplicateIndexes.length ? { [FH_FIELD_ERROR.DUPLICATE_THRESHOLD]: true } : null;
  };
}

// Composes the validators that depend only on the field config. `required` is intentionally
// excluded — the rules engine re-applies it on every recompute.
export function buildStaticValidators(field: CompanyFieldDescriptor): ValidatorFn[] {
  const validators: ValidatorFn[] = [];

  if (field.validation === FH_FIELD_VALIDATION.EMAIL) {
    validators.push(fhEmailValidator());
  }
  if (field.validation === FH_FIELD_VALIDATION.URL) {
    validators.push(fhUrlValidator());
  }
  // `field.regex` is already null for selects — the factory drops it there.
  if (field.regex) {
    validators.push(fhRegexValidator(field.regex));
  }
  if (field.type === FH_FIELD_TYPE.NUMBER) {
    if (field.min !== null) {
      validators.push(Validators.min(field.min));
    }
    if (field.max !== null) {
      validators.push(Validators.max(field.max));
    }
    if (field.decimals !== null) {
      validators.push(fhDecimalsValidator(field.decimals));
    }
  }

  return validators;
}
